import { useState } from "react";
import { TECHNOLOGIES, TECHNOLOGY_BY_ID } from "../game-data/technologies";
import { BRANCH_CAPABILITY_LABELS, CAPABILITY_LABELS, technologyLevelLabel } from "../game-data/terminology";
import { useGame, useLiveGameTime } from "../app/GameContext";
import type { TechBranch, TechnologyNode } from "../shared/game-types";
import {
  ActionButton,
  ChoiceGroup,
  Meter,
  PageHeader,
  formatDeadline,
  formatDurationWithReal,
  formatNumber,
  progressBetween,
} from "../components/GameUi";

const branches: TechBranch[] = ["industry", "navigation", "military"];

export function TechnologyPage() {
  const { state, secondsPerGameYear } = useGame();
  const liveGameTime = useLiveGameTime();
  const [selectedBranch, setSelectedBranch] = useState<TechBranch>("industry");
  if (!state) return null;
  const current = state.technologies.current;
  const currentDefinition = current ? TECHNOLOGY_BY_ID[current.techId] : undefined;

  return (
    <div className="page-stack">
      <PageHeader eyebrow="RESEARCH // 工业 · 航行 · 军事" title="科技树" description="单研究槽配合排队计划持续推进；三条分支不存在永久互斥，服务器核验科研点、能力等级与前置。" />

      <section className="panel research-console" aria-labelledby="current-research-heading">
        <div className="section-heading">
          <div><p className="eyebrow">ACTIVE RESEARCH</p><h2 id="current-research-heading">研究控制台</h2></div>
          <span className="status-chip">科研点库存 {formatNumber(state.resources.research)}</span>
        </div>
        {current && currentDefinition ? (
          <div className="active-research">
            <div>
              <span className="tech-id">{technologyLevelLabel(currentDefinition.id)}</span>
              <h3>{currentDefinition.name}</h3>
              <p>{currentDefinition.description}</p>
            </div>
            <div>
              <span>时间进度 {Math.round(progressBetween(current.startedAtGameYear, current.completesAtGameYear, liveGameTime) * 100)}%</span>
              <Meter value={progressBetween(current.startedAtGameYear, current.completesAtGameYear, liveGameTime)} max={1} label="研究时间进度" />
              <small>{formatDeadline(current.completesAtGameYear, liveGameTime, secondsPerGameYear)}</small>
              <small>已投入 {formatNumber(current.invested)} 科研点</small>
            </div>
            <ActionButton className="button danger-button" action={{ type: "CANCEL_RESEARCH" }} confirm="取消当前研究？只返还 50% 已投入科研点。">取消研究</ActionButton>
          </div>
        ) : <p className="empty-state">当前没有研究项目。选择下方任意已解锁节点开始。</p>}
        {state.technologies.queue.length > 0 && (
          <div className="queue-row"><span>研究队列</span><ol>{state.technologies.queue.map((id) => <li key={id}><b>{technologyLevelLabel(id)}</b> {TECHNOLOGY_BY_ID[id]?.name ?? "未知科技"}</li>)}</ol></div>
        )}
      </section>

      <div className="tech-legend" aria-label="科技状态图例">
        <span><i className="dot complete" />已完成</span><span><i className="dot active" />研究中</span><span><i className="dot available" />可研究</span><span><i className="dot locked" />未解锁</span>
      </div>

      <div className="focus-picker">
        <ChoiceGroup
          label="查看科技分支"
          value={selectedBranch}
          onChange={setSelectedBranch}
          options={branches.map((branch) => ({ value: branch, label: BRANCH_CAPABILITY_LABELS[branch], description: `当前 ${branchLevel(state, branch)}级` }))}
        />
        <p>{branchDescriptions[selectedBranch]}</p>
      </div>

      <section className="tech-branch" aria-labelledby={`branch-${selectedBranch}`}>
        <div className="section-heading branch-heading">
          <div><p className="eyebrow">{selectedBranch.toUpperCase()} BRANCH</p><h2 id={`branch-${selectedBranch}`}>{BRANCH_CAPABILITY_LABELS[selectedBranch]}</h2></div>
          <span className="status-chip">当前 {branchLevel(state, selectedBranch)}级</span>
        </div>
        <div className="tech-tree">
          {TECHNOLOGIES.filter((technology) => technology.branch === selectedBranch).map((technology) => (
            <TechNode key={technology.id} technology={technology} />
          ))}
        </div>
      </section>
    </div>
  );
}

function TechNode({ technology }: { technology: TechnologyNode }) {
  const { state, secondsPerGameYear } = useGame();
  if (!state) return null;
  const completed = state.technologies.completed.includes(technology.id);
  const current = state.technologies.current?.techId === technology.id;
  const queued = state.technologies.queue.includes(technology.id);
  const prerequisitesMet = technology.prerequisites.every((id) => state.technologies.completed.includes(id));
  const planned = new Set([
    ...state.technologies.completed,
    ...(state.technologies.current ? [state.technologies.current.techId] : []),
    ...state.technologies.queue,
  ]);
  const prerequisitesPlanned = technology.prerequisites.every((id) => planned.has(id));
  const levelsMet = state.player.levels.energy >= technology.requiredEnergy && state.player.levels.materials >= technology.requiredMaterials;
  const canSchedule = state.technologies.current ? prerequisitesPlanned : prerequisitesMet && levelsMet;
  const available = canSchedule && !completed && !current && !queued;
  const status = completed ? "complete" : current ? "active" : queued ? "queued" : available ? "available" : "locked";
  const action = state.technologies.current
    ? { type: "QUEUE_RESEARCH" as const, techId: technology.id }
    : { type: "START_RESEARCH" as const, techId: technology.id };

  return (
    <article className={`tech-node ${status}`} data-tech-id={technology.id}>
      <div className="tech-node-heading">
        <span className="tech-id">{technologyLevelLabel(technology.id)}</span>
        <span>{statusLabel[status]}</span>
      </div>
      <h3>{technology.name}</h3>
      <p>{technology.description}</p>
      <dl className="inline-specs">
        <div><dt>科研点</dt><dd>{formatNumber(technology.cost)}</dd></div>
        <div><dt>研究周期</dt><dd>{formatDurationWithReal(technology.durationYears, secondsPerGameYear)}</dd></div>
      </dl>
      <details className="tech-requirements">
        <summary>查看前置条件与能力要求</summary>
        <dl className="detail-list">
          <div><dt>能力要求</dt><dd>{CAPABILITY_LABELS.energy} {technology.requiredEnergy}级 · {CAPABILITY_LABELS.materials} {technology.requiredMaterials}级</dd></div>
          <div><dt>前置科技</dt><dd>{technology.prerequisites.map(technologyLevelLabel).join("、") || "无"}</dd></div>
        </dl>
      </details>
      {available && <ActionButton className="button primary full" action={action}>{state.technologies.current ? "加入队列" : "开始研究"}</ActionButton>}
      {!available && !completed && !current && !queued && <small className="lock-reason">{
        state.technologies.current && !prerequisitesPlanned
          ? "需要先完成或排入前置科技"
          : !prerequisitesMet
            ? "需要完成前置科技"
            : `需要${CAPABILITY_LABELS.energy} ${technology.requiredEnergy}级和${CAPABILITY_LABELS.materials} ${technology.requiredMaterials}级`
      }</small>}
    </article>
  );
}

function branchLevel(state: NonNullable<ReturnType<typeof useGame>["state"]>, branch: TechBranch) {
  return state.player.levels[branch];
}

const statusLabel: Record<string, string> = {
  complete: "已完成",
  active: "研究中",
  queued: "已排队",
  available: "可研究",
  locked: "锁定",
};

const branchDescriptions: Record<TechBranch, string> = {
  industry: "提升生产、材料与大型工程能力，是扩张和维持文明的基础。",
  navigation: "提升推进、探测和跨恒星航行能力，决定可以抵达多远。",
  military: "提升舰队、生存与作战能力，用于护航、边境控制和有限战争。",
};
